/**
 * Landing section that points visitors to the most relevant next pages.
 *
 * What: Renders a visible set of related route cards (Primary / Secondary /
 * Blog on the homepage; school pages pass their own set).
 * Why: Improves internal linking, helps users choose a starting point, and
 * exposes route context to AI agents.
 * When: Update when the best next-step routes or their descriptions change.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import { landingSectionIds } from '@/components/landing/landingContent';
import LandingSection from '@/components/landing/LandingSection';
import type { LandingSectionTone } from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

interface RelatedLinksContent {
  eyebrow: string;
  title: string;
  body?: string;
  links: Array<{ label: string; description: string; path: string }>;
}

interface LandingRelatedLinksSectionProps {
  id?: string;
  tone?: LandingSectionTone;
  content: RelatedLinksContent;
  /** 'center' matches the homepage design; 'start' keeps the school-page layout. */
  align?: 'start' | 'center';
}

function LandingRelatedLinksSection({
  content,
  id = landingSectionIds.relatedLinks,
  tone = 'muted',
  align = 'start',
}: LandingRelatedLinksSectionProps) {
  const titleId = `${id}-title`;
  const centered = align === 'center';

  return (
    <LandingSection id={id} tone={tone} ariaLabelledby={titleId}>
      <Stack spacing={centered ? 0 : { xs: 3.5, sm: 4 }}>
        {centered ? (
          <LandingCenteredSectionHead
            titleId={titleId}
            eyebrow={content.eyebrow}
            title={content.title}
            body={content.body}
          />
        ) : (
          <Stack spacing={1.5} sx={{ maxWidth: { xs: '100%', sm: 760 } }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 2,
                color: colors.accentCobalt,
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              {content.eyebrow}
            </Typography>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                fontSize: { xs: 28, sm: 32, md: 36 },
                fontWeight: 800,
                letterSpacing: -0.5,
                color: colors.ink,
                lineHeight: 1.15,
              }}
            >
              {content.title}
            </Typography>
            {content.body ? (
              <Typography
                variant="body1"
                sx={{
                  color: colors.inkMuted,
                  lineHeight: 1.65,
                  fontSize: { xs: 15, sm: 16 },
                }}
              >
                {content.body}
              </Typography>
            ) : null}
          </Stack>
        )}

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2.25, sm: 2.5 },
            width: '100%',
            maxWidth: centered ? 960 : 'none',
            mx: centered ? 'auto' : 0,
          }}
        >
          {content.links.map(link => (
            <Paper
              key={link.path}
              component={RouterLink}
              to={link.path}
              elevation={0}
              sx={{
                flex: '1 1 240px',
                minWidth: 0,
                p: '22px 24px',
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.soft,
                textDecoration: 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: shadows.lift,
                },
              }}
            >
              <Stack spacing={1.125}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      color: colors.ink,
                      fontWeight: 800,
                      fontSize: { xs: 16, sm: 17 },
                      letterSpacing: '-0.01em',
                      lineHeight: 1.3,
                    }}
                  >
                    {link.label}
                  </Typography>
                  <ArrowIcon
                    sx={{
                      color: colors.accentCobalt,
                      flexShrink: 0,
                      fontSize: 20,
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.inkMuted,
                    lineHeight: 1.6,
                    fontSize: { xs: 12.5, sm: 13 },
                    fontWeight: 500,
                  }}
                >
                  {link.description}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Stack>
    </LandingSection>
  );
}

export default LandingRelatedLinksSection;
